import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { CheckCircle, ChevronRight } from "lucide-react";
import { SERVICES, CHANTIERS, ZONES } from "../data/siteData";
import { useSeo } from "../hooks/useSeo";
import { PageHeader, CtaBand } from "../components/PageHeader";

const RelatedChantiers = ({ list }) => (
  <div className="reveal">
    <h2 className="font-['Oswald'] text-2xl font-bold text-[#F5EFE3] mb-6">Chantiers de référence</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {list.map((c) => (
        <Link key={c.slug} to={`/realisations/${c.slug}`} data-testid={`service-related-${c.slug}`} className="group flex items-center justify-between bg-[#141210] border border-[#2E2820] hover:border-[#D4A537]/60 p-5 transition-colors">
          <div>
            <p className="font-semibold text-[#F5EFE3]">{c.chantier}</p>
            <p className="text-sm text-[#8C8578]">{c.periode} · {c.client}</p>
          </div>
          <ChevronRight className="w-5 h-5 text-[#D4A537] group-hover:translate-x-1 transition-transform" />
        </Link>
      ))}
    </div>
  </div>
);

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = SERVICES.find((x) => x.slug === slug);
  useSeo(s ? s.seoTitle : "Services", s?.short, `/services/${slug}`);
  if (!s) return <Navigate to="/services" replace />;
  const related = CHANTIERS.filter((c) => s.operations.includes(c.operation)).slice(0, 6);

  return (
    <main data-testid={`service-page-${s.slug}`} className="pt-20">
      <PageHeader crumbs={[{ label: "Services", to: "/services" }, { label: s.titre }]} label="Courant Fort · Île-de-France" title={s.seoTitle}>
        <p className="text-white/70 text-lg max-w-2xl animate-fade-in-up animation-delay-100">{s.short}</p>
      </PageHeader>
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-1 lg:grid-cols-5 gap-14">
          <div className="lg:col-span-3 space-y-5 text-[#A8A091] text-lg leading-relaxed reveal">
            {s.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <div className="lg:col-span-2 reveal">
            <img src={s.image} alt={s.titre} className="w-full h-72 object-cover mb-8 border border-[#2E2820]" />
            <ul className="space-y-3">
              {s.details.map((d) => (
                <li key={d} className="flex items-center gap-3 text-[#F5EFE3]"><CheckCircle className="w-5 h-5 text-[#D4A537] flex-shrink-0" />{d}</li>
              ))}
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 mt-20 space-y-16">
          {related.length > 0 && <RelatedChantiers list={related} />}
          <div className="reveal">
            <h2 className="font-['Oswald'] text-2xl font-bold text-[#F5EFE3] mb-6">{s.titre} par département</h2>
            <div className="flex flex-wrap gap-3">
              {ZONES.map((z) => (
                <Link key={z.slug} to={`/electricien-btp/${z.slug}`} className="border border-[#2E2820] hover:border-[#D4A537] hover:text-[#D4A537] text-[#A8A091] px-4 py-2 text-sm transition-colors">{z.nom} ({z.code})</Link>
              ))}
            </div>
          </div>
        </div>
      </section>
      <CtaBand title={`Besoin d'un ${s.titre.toLowerCase()} ?`} />
    </main>
  );
}
